import { defineStore } from 'pinia'
import { ref } from 'vue'
import router from '@/router'
import { authApi } from '@/api'

export interface MenuItem {
  id: number
  parent_id: number | null
  name: string
  path: string
  component?: string
  redirect?: string
  title: string
  icon?: string
  sort: number
  type: string
  status: string
  hidden?: boolean
  keep_alive?: boolean
  affix?: boolean
  children?: MenuItem[]
}

export const useMenuStore = defineStore('menu', () => {
  const menus = ref<MenuItem[]>([])
  const permissions = ref<string[]>([])
  let pageLoadSynced = false
  let syncPromise: Promise<boolean> | null = null

  // 设置菜单
  function setMenus(newMenus: MenuItem[]) {
    console.log('setMenus called with:', newMenus)
    if (!Array.isArray(newMenus)) {
      console.error('setMenus: newMenus is not an array', newMenus)
      return
    }
    menus.value = newMenus
    localStorage.setItem('menus', JSON.stringify(newMenus))
    // 动态添加路由
    addDynamicRoutes(newMenus)
  }

  // 设置权限
  function setPermissions(newPermissions: string[]) {
    permissions.value = newPermissions
    localStorage.setItem('permissions', JSON.stringify(newPermissions))
  }

  // 从 localStorage 恢复（仅作首屏占位，刷新后会由 syncFromServer 覆盖）
  function restoreFromStorage() {
    const savedMenus = localStorage.getItem('menus')
    const savedPermissions = localStorage.getItem('permissions')

    if (savedMenus) {
      menus.value = JSON.parse(savedMenus)
      addDynamicRoutes(menus.value)
    }

    if (savedPermissions) {
      permissions.value = JSON.parse(savedPermissions)
    }
  }

  /** 每次整页刷新后从服务端同步菜单与权限（同一会话内只拉一次） */
  async function syncFromServer(): Promise<boolean> {
    if (pageLoadSynced) {
      return true
    }
    if (syncPromise) {
      return syncPromise
    }

    syncPromise = (async () => {
      const token = localStorage.getItem('token')
      if (!token) {
        return false
      }

      try {
        const [menuData, permData] = await Promise.all([
          authApi.getMenus(),
          authApi.getPermCode().catch(() => []),
        ])

        const nextMenus = Array.isArray(menuData) ? menuData : []
        setMenus(nextMenus)

        if (Array.isArray(permData)) {
          setPermissions(permData)
        }

        pageLoadSynced = true
        return true
      } catch (error) {
        console.error('Failed to sync menus from server:', error)
        return false
      } finally {
        syncPromise = null
      }
    })()

    return syncPromise
  }

  /** 强制重新拉取（角色变更后手动刷新页面时由路由守卫调用） */
  async function forceSyncFromServer(): Promise<boolean> {
    pageLoadSynced = false
    return syncFromServer()
  }

  // 检查权限
  function hasPermission(permission: string): boolean {
    return permissions.value.includes(permission)
  }

  // 检查是否有任意权限
  function hasAnyPermission(perms: string[]): boolean {
    return perms.some(perm => permissions.value.includes(perm))
  }

  // 清空菜单和权限
  function clear() {
    menus.value = []
    permissions.value = []
    pageLoadSynced = false
    syncPromise = null
    localStorage.removeItem('menus')
    localStorage.removeItem('permissions')
  }

  // 动态添加路由
  function addDynamicRoutes(menuList: MenuItem[]) {
    // 递归处理菜单，生成路由
    const processMenu = (menu: MenuItem, parentPath = ''): any => {
      const routePath = menu.path.startsWith('/') ? menu.path : `${parentPath}/${menu.path}`

      // 如果是目录类型，只处理子菜单
      if (menu.type === 'directory') {
        const children: any[] = []
        if (menu.children && menu.children.length > 0) {
          menu.children.forEach(child => {
            const childRoute = processMenu(child, routePath)
            if (childRoute) {
              children.push(childRoute)
            }
          })
        }
        return children.length > 0 ? { path: routePath, children } : null
      }

      // 构建路由配置
      const route: any = {
        path: routePath,
        name: menu.name,
        meta: {
          title: menu.title,
          icon: menu.icon,
          hidden: menu.hidden || false,
          keepAlive: menu.keep_alive || false,
          affix: menu.affix || false,
        }
      }

      // 处理组件路径
      if (menu.component) {
        // 如果是 LAYOUT，表示布局组件
        if (menu.component === 'LAYOUT') {
          route.component = () => import('@/layouts/MainLayout.vue')
        } else {
          // 动态导入组件
          try {
            route.component = () => import(`@/views/${menu.component}.vue`)
          } catch (e) {
            console.warn(`Component not found: ${menu.component}`)
            // 如果组件不存在，使用默认组件
            route.component = () => import('@/views/Home.vue')
          }
        }
      } else {
        // 如果没有指定组件，使用默认组件
        route.component = () => import('@/views/Home.vue')
      }

      // 处理重定向
      if (menu.redirect) {
        route.redirect = menu.redirect
      }

      // 处理子菜单
      if (menu.children && menu.children.length > 0) {
        route.children = []
        menu.children.forEach(child => {
          const childRoute = processMenu(child, routePath)
          if (childRoute) {
            route.children.push(childRoute)
          }
        })
      }

      return route
    }

    // 找到主布局路由（path 为 '/' 的路由）
    const mainRoute = router.getRoutes().find(r => r.path === '/' && r.children)

    if (mainRoute) {
      // 清空现有动态路由
      const staticRoutes = mainRoute.children?.filter(r => r.meta?.isStatic) || []

      // 处理菜单，生成动态路由
      const dynamicRoutes: any[] = []
      menuList.forEach(menu => {
        const route = processMenu(menu)
        if (route) {
          if (Array.isArray(route)) {
            dynamicRoutes.push(...route)
          } else {
            dynamicRoutes.push(route)
          }
        }
      })

      // 合并静态路由和动态路由
      const allRoutes = [...staticRoutes, ...dynamicRoutes]

      // 移除旧路由并添加新路由
      mainRoute.children = allRoutes
    }
  }

  return {
    menus,
    permissions,
    setMenus,
    setPermissions,
    restoreFromStorage,
    syncFromServer,
    forceSyncFromServer,
    hasPermission,
    hasAnyPermission,
    clear
  }
})
